import type * as openapi from '@/helpers/api/types/openapiOverrides';

import { computed, ref } from 'vue';
import { defineStore } from 'pinia';

import {
  fetchGetCatalogAttributes,
  fetchGetCatalogBases,
  fetchGetCatalogDriverTypes,
  fetchGetCatalogDrivers,
  fetchGetCatalogFleets,
  fetchGetCatalogGoodsTypes,
  fetchGetCatalogOffsiders,
  fetchGetCatalogServiceAreas,
  fetchGetCatalogServiceCodes,
  fetchGetCatalogServiceGroups,
  fetchGetCatalogSites,
  fetchGetCatalogZones,
} from '@/helpers/fetchers';

const usePrivateStaticStore = defineStore('_static', () => {
  const attributes = ref<openapi.GetCatalogAttributes_200>([]);
  const bases = ref<openapi.GetCatalogBases_200>([]);
  const driverTypes = ref<openapi.GetCatalogDriverTypes_200>([]);
  const drivers = ref<openapi.GetCatalogDrivers_200>([]);
  const offsiders = ref<openapi.GetCatalogOffsiders_200>([]);
  const fleets = ref<openapi.GetCatalogFleets_200>([]);
  const productTypes = ref<openapi.GetCatalogGoodsTypes_200>([]);
  const serviceAreas = ref<openapi.GetCatalogServiceAreas_200>([]);
  const serviceCodes = ref<openapi.GetCatalogServiceCodes_200>([]);
  const serviceGroups = ref<openapi.GetCatalogServiceGroups_200>([]);
  const sites = ref<openapi.GetCatalogSites_200>([]);
  const zones = ref<openapi.GetCatalogZones_200>([]);
  function $reset(): void {
    attributes.value = [];
    bases.value = [];
    driverTypes.value = [];
    drivers.value = [];
    offsiders.value = [];
    fleets.value = [];
    productTypes.value = [];
    serviceAreas.value = [];
    serviceCodes.value = [];
    serviceGroups.value = [];
    sites.value = [];
    zones.value = [];
  }
  return {
    attributes,
    bases,
    driverTypes,
    drivers,
    offsiders,
    fleets,
    productTypes,
    serviceAreas,
    serviceCodes,
    serviceGroups,
    sites,
    zones,
    $reset,
  };
});

export const useStaticStore = defineStore('static', () => {
  const _staticStore = usePrivateStaticStore();

  function $reset(): void {
    _staticStore.$reset();
  }

  function clearStaticState(): void {
    $reset();
  }

  const getAttributes = computed(() => _staticStore.attributes);
  const getBases = computed(() => _staticStore.bases);
  const getDriverTypes = computed(() => _staticStore.driverTypes);
  const getDrivers = computed(() => _staticStore.drivers);
  const getOffsiders = computed(() => _staticStore.offsiders);
  const getFleets = computed(() => _staticStore.fleets);
  const getProductTypes = computed(() => _staticStore.productTypes);
  const getServiceAreas = computed(() => _staticStore.serviceAreas);
  const getServiceCodes = computed(() => _staticStore.serviceCodes);
  const getServiceGroups = computed(() => _staticStore.serviceGroups);
  const getSites = computed(() => _staticStore.sites);
  const getZones = computed(() => _staticStore.zones);

  const getAllStaticData = computed(() => {
    return {
      attributes: _staticStore.attributes,
      bases: _staticStore.bases,
      driverTypes: _staticStore.driverTypes,
      drivers: _staticStore.drivers,
      offsiders: _staticStore.offsiders,
      fleets: _staticStore.fleets,
      productTypes: _staticStore.productTypes,
      serviceAreas: _staticStore.serviceAreas,
      serviceCodes: _staticStore.serviceCodes,
      serviceGroups: _staticStore.serviceGroups,
      sites: _staticStore.sites,
      zones: _staticStore.zones,
    };
  });

  const isAllStaticDataLoaded = computed(() => {
    return (
      _staticStore.attributes.length &&
      _staticStore.bases.length &&
      _staticStore.driverTypes.length &&
      _staticStore.drivers.length &&
      _staticStore.offsiders.length &&
      _staticStore.fleets.length &&
      _staticStore.productTypes.length &&
      _staticStore.serviceAreas.length &&
      _staticStore.serviceCodes.length &&
      _staticStore.serviceGroups.length &&
      _staticStore.sites.length &&
      _staticStore.zones.length
    );
  });

  async function requestAttributes(): Promise<void> {
    await fetchGetCatalogAttributes(undefined).then((attributes) => {
      _staticStore.attributes = attributes;
    });
  }

  async function requestBases(): Promise<void> {
    await fetchGetCatalogBases(undefined).then((bases) => {
      _staticStore.bases = bases;
    });
  }

  async function requestDriverTypes(): Promise<void> {
    await fetchGetCatalogDriverTypes(undefined).then((driverTypes) => {
      _staticStore.driverTypes = driverTypes;
    });
  }

  async function requestDrivers(): Promise<void> {
    await fetchGetCatalogDrivers(undefined).then((drivers) => {
      _staticStore.drivers = drivers;
    });
  }

  async function requestOffsiders(): Promise<void> {
    await fetchGetCatalogOffsiders(undefined).then((offsiders) => {
      _staticStore.offsiders = offsiders;
    });
  }

  async function requestFleets(): Promise<void> {
    await fetchGetCatalogFleets(undefined).then((fleets) => {
      _staticStore.fleets = fleets;
    });
  }

  async function requestProductTypes(): Promise<void> {
    await fetchGetCatalogGoodsTypes(undefined).then((productTypes) => {
      _staticStore.productTypes = productTypes;
    });
  }

  async function requestServiceAreas(): Promise<void> {
    await fetchGetCatalogServiceAreas(undefined).then((serviceAreas) => {
      _staticStore.serviceAreas = serviceAreas;
    });
  }

  async function requestServiceCodes(): Promise<void> {
    await fetchGetCatalogServiceCodes(undefined).then((serviceCodes) => {
      _staticStore.serviceCodes = serviceCodes;
    });
  }

  async function requestServiceGroups(): Promise<void> {
    await fetchGetCatalogServiceGroups(undefined).then((serviceGroups) => {
      _staticStore.serviceGroups = serviceGroups;
    });
  }

  async function requestSites(): Promise<void> {
    await fetchGetCatalogSites(undefined).then((sites) => {
      _staticStore.sites = sites;
    });
  }

  async function requestZones(): Promise<void> {
    await fetchGetCatalogZones(undefined).then((zones) => {
      _staticStore.zones = zones;
    });
  }

  async function requestAllStaticData(): Promise<void> {
    await Promise.all([
      requestAttributes(),
      requestBases(),
      requestDriverTypes(),
      requestDrivers(),
      requestOffsiders(),
      requestFleets(),
      requestProductTypes(),
      requestServiceAreas(),
      requestServiceCodes(),
      requestServiceGroups(),
      requestSites(),
      requestZones(),
    ]);
  }

  return {
    $reset,
    clearStaticState,
    getAttributes,
    getBases,
    getDriverTypes,
    getDrivers,
    getOffsiders,
    getFleets,
    getProductTypes,
    getServiceAreas,
    getServiceCodes,
    getServiceGroups,
    getSites,
    getZones,
    getAllStaticData,
    isAllStaticDataLoaded,
    requestAttributes,
    requestBases,
    requestDriverTypes,
    requestDrivers,
    requestOffsiders,
    requestFleets,
    requestProductTypes,
    requestServiceAreas,
    requestServiceCodes,
    requestServiceGroups,
    requestSites,
    requestZones,
    requestAllStaticData,
  };
});
